import type { ChannelValues } from '../animation/timeline';
import type { SceneEffects } from '../av/reactions';
import { MAX_REVIEW_FRAMES } from './timestamps';

/**
 * Visual review frame capture (Phase 5C.1).
 *
 * Frames come from the video-export renderer (`SessionFrameRenderer`, created by the
 * caller from a frozen snapshot): the same scene, characters, layers, Look effects at full
 * quality and framing as a video export. One renderer, one frame at a time: draw, read
 * the drawn values, encode to PNG, keep only the compressed Blob. Rendering and encoding
 * are timed separately. Cancelling stops between steps; the renderer is destroyed on
 * success, failure and cancellation alike.
 */

/** Output sizes by long edge; the short edge follows the scene's aspect ratio. */
export const REVIEW_LONG_EDGES = [1280, 960, 640] as const;
export type ReviewLongEdge = (typeof REVIEW_LONG_EDGES)[number];
export const DEFAULT_LONG_EDGE: ReviewLongEdge = 1280;
/** One PNG larger than this is refused (a 1280×720 frame is 0.1–0.7 MB in practice). */
export const MAX_FRAME_BYTES = 8 * 1024 * 1024;
/** All frames together. */
export const MAX_TOTAL_BYTES = 40 * 1024 * 1024;

export class CaptureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CaptureError';
  }
}

/** The output size for a scene at a long edge: aspect kept, even numbers, at least 16 px. */
export function reviewSize(sceneW: number, sceneH: number, longEdge: number): { width: number; height: number } {
  if (!REVIEW_LONG_EDGES.includes(longEdge as ReviewLongEdge)) throw new CaptureError(`Choose a size of ${REVIEW_LONG_EDGES.join(', ')} pixels.`);
  const even = (v: number) => Math.max(16, 2 * Math.round(v / 2));
  return sceneW >= sceneH ? { width: longEdge, height: even((longEdge * sceneH) / sceneW) } : { width: even((longEdge * sceneW) / sceneH), height: longEdge };
}

/** What capture needs from a renderer (`SessionFrameRenderer` provides it). */
export interface ReviewFrameSource {
  readonly canvas: HTMLCanvasElement | OffscreenCanvas;
  renderFrame(seconds: number): void;
  shown(): { values: Map<string, ChannelValues>; effects: SceneEffects | null; frame: number };
  destroy(): void;
}

export interface CapturedFrame {
  /** "f01" … "f08". */
  id: string;
  index: number;
  /** Source position drawn. */
  seconds: number;
  /** Animation frame drawn (the source's animation; the open-ended counter without one). */
  frame: number;
  blob: Blob;
  bytes: number;
  renderMs: number;
  encodeMs: number;
  /** Animated or reacting objects and their drawn values in this frame. */
  values: Record<string, ChannelValues>;
  effects: SceneEffects | null;
}

export interface CaptureResult {
  frames: CapturedFrame[];
  width: number;
  height: number;
  createMs: number;
  totalBytes: number;
}

export type CapturePhase = 'preparing' | 'rendering' | 'encoding';

export interface CaptureOptions {
  width: number;
  height: number;
  signal?: AbortSignal;
  onProgress?: (p: { phase: CapturePhase; done: number; total: number }) => void;
  /** Test seam: PNG encoding (default: canvas.toBlob / convertToBlob). */
  encode?: (canvas: HTMLCanvasElement | OffscreenCanvas) => Promise<Blob>;
  now?: () => number;
}

const abortError = () => new DOMException('The review export was cancelled.', 'AbortError');

export async function encodePng(canvas: HTMLCanvasElement | OffscreenCanvas): Promise<Blob> {
  if ('convertToBlob' in canvas) return canvas.convertToBlob({ type: 'image/png' });
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new CaptureError('The browser could not encode a frame as PNG.'))), 'image/png'));
}

/**
 * Forces the GPU to finish the drawn frame (a 1-pixel read-back), so the render time
 * measured does not leak into the encoding time.
 */
function finishGpu(canvas: HTMLCanvasElement | OffscreenCanvas, sink: CanvasRenderingContext2D | null): void {
  if (!sink) return;
  sink.drawImage(canvas, 0, 0, 1, 1, 0, 0, 1, 1);
  sink.getImageData(0, 0, 1, 1);
}

export async function captureReviewFrames(createSource: () => Promise<ReviewFrameSource>, timestamps: readonly number[], options: CaptureOptions): Promise<CaptureResult> {
  if (timestamps.length === 0) throw new CaptureError('Choose at least one frame.');
  if (timestamps.length > MAX_REVIEW_FRAMES) throw new CaptureError(`At most ${MAX_REVIEW_FRAMES} frames can be exported for review.`);
  const now = options.now ?? (() => performance.now());
  const encode = options.encode ?? encodePng;
  const { signal, onProgress } = options;
  const total = timestamps.length;
  if (signal?.aborted) throw abortError();
  onProgress?.({ phase: 'preparing', done: 0, total });
  const t0 = now();
  const source = await createSource();
  const createMs = now() - t0;
  const sinkCanvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (sinkCanvas) sinkCanvas.width = sinkCanvas.height = 1;
  const sink = (sinkCanvas?.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null | undefined) ?? null;
  try {
    const frames: CapturedFrame[] = [];
    let totalBytes = 0;
    for (let i = 0; i < total; i++) {
      if (signal?.aborted) throw abortError();
      const seconds = timestamps[i]!;
      onProgress?.({ phase: 'rendering', done: i, total });
      const r0 = now();
      source.renderFrame(seconds);
      finishGpu(source.canvas, sink);
      const renderMs = now() - r0;
      const shown = source.shown();
      onProgress?.({ phase: 'encoding', done: i, total });
      const e0 = now();
      const blob = await encode(source.canvas);
      const encodeMs = now() - e0;
      if (signal?.aborted) throw abortError();
      if (blob.size > MAX_FRAME_BYTES) throw new CaptureError(`Frame ${i + 1} is ${(blob.size / 1048576).toFixed(1)} MB as PNG, over the ${MAX_FRAME_BYTES / 1048576} MB limit. Choose a smaller size.`);
      totalBytes += blob.size;
      if (totalBytes > MAX_TOTAL_BYTES) throw new CaptureError(`The frames passed ${MAX_TOTAL_BYTES / 1048576} MB after frame ${i + 1}. Choose fewer frames or a smaller size.`);
      frames.push({
        id: `f${String(i + 1).padStart(2, '0')}`,
        index: i,
        seconds,
        frame: shown.frame,
        blob,
        bytes: blob.size,
        renderMs,
        encodeMs,
        values: Object.fromEntries([...shown.values.entries()].sort(([a], [b]) => a.localeCompare(b))),
        effects: shown.effects,
      });
    }
    onProgress?.({ phase: 'encoding', done: total, total });
    return { frames, width: options.width, height: options.height, createMs, totalBytes };
  } finally {
    source.destroy();
  }
}
